import { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, Polyline, CircleMarker, Popup } from "react-leaflet";
import api from "../lib/api";
import { X } from "@phosphor-icons/react";

// Route replay: shows the GPS trail for a truck between `from` and `to` (inclusive of `from`,
// exclusive of `to+1 day`). Max range = 30 days, enforced client-side and server-side.
export default function RouteReplayModal({ vehicleNumber, onClose, initialFrom, initialTo }) {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(initialFrom || today);
  const [to, setTo] = useState(initialTo || today);
  const [snaps, setSnaps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const clampedRange = useMemo(() => {
    // Ensure from ≤ to and range ≤ 30 days; return a safe [from, to].
    if (!from || !to) return { from, to, valid: false };
    let f = new Date(from), t = new Date(to);
    if (t < f) t = f;
    const days = Math.round((t - f) / 86400000);
    if (days > 30) f = new Date(t.getTime() - 30 * 86400000);
    return {
      from: f.toISOString().slice(0, 10),
      to: t.toISOString().slice(0, 10),
      valid: true,
    };
  }, [from, to]);

  useEffect(() => {
    if (!vehicleNumber || !clampedRange.valid) return;
    setLoading(true);
    setErr("");
    const since = `${clampedRange.from}T00:00:00+00:00`;
    // Server treats `until` as exclusive < — include the whole `to` day.
    const endDate = new Date(clampedRange.to); endDate.setDate(endDate.getDate() + 1);
    const until = `${endDate.toISOString().slice(0, 10)}T00:00:00+00:00`;
    api.get("/gps/history", { params: { vehicle_number: vehicleNumber, since, until, limit: 5000 } })
      .then(({ data }) => setSnaps(data.snapshots || []))
      .catch((e) => { setSnaps([]); setErr(e?.response?.data?.detail || "Failed to load GPS trail"); })
      .finally(() => setLoading(false));
  }, [vehicleNumber, clampedRange.from, clampedRange.to, clampedRange.valid]);

  const path = snaps.map((s) => [s.lat, s.lng]);
  const distKm = useMemo(() => {
    if (snaps.length < 2) return 0;
    let total = 0;
    const R = 6371;
    for (let i = 1; i < snaps.length; i++) {
      const a = snaps[i - 1], b = snaps[i];
      const rp1 = (a.lat * Math.PI) / 180, rp2 = (b.lat * Math.PI) / 180;
      const dp = ((b.lat - a.lat) * Math.PI) / 180, dl = ((b.lng - a.lng) * Math.PI) / 180;
      const h = Math.sin(dp / 2) ** 2 + Math.cos(rp1) * Math.cos(rp2) * Math.sin(dl / 2) ** 2;
      total += 2 * R * Math.asin(Math.sqrt(h));
    }
    return total;
  }, [snaps]);

  const center = path.length ? path[0] : [6, 0.5];
  const preset = (days) => {
    const t = new Date();
    const f = new Date(); f.setDate(f.getDate() - (days - 1));
    setFrom(f.toISOString().slice(0, 10));
    setTo(t.toISOString().slice(0, 10));
  };

  return (
    <div className="fixed inset-0 z-[1200] bg-slate-900/60 grid place-items-center p-4" onClick={onClose} data-testid="route-replay-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col" style={{ maxHeight: "92vh" }}>
        <div className="p-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Route replay · up to 30 days</div>
            <h3 className="font-heading font-black text-xl tracking-tight" data-testid="route-replay-title">{vehicleNumber}</h3>
          </div>
          <div className="flex items-center gap-1.5 text-[10px]">
            {[
              { d: 1, l: "1d" },
              { d: 7, l: "7d" },
              { d: 14, l: "14d" },
              { d: 30, l: "30d" },
            ].map((p) => (
              <button key={p.d} onClick={() => preset(p.d)} data-testid={`route-replay-preset-${p.l}`} className="px-2 py-1 border border-slate-300 rounded hover:bg-slate-50 font-mono-data font-semibold">{p.l}</button>
            ))}
          </div>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="route-replay-from" className="border border-slate-300 rounded px-2 py-1 text-sm font-mono-data" />
          <span className="text-slate-400">→</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="route-replay-to" className="border border-slate-300 rounded px-2 py-1 text-sm font-mono-data" />
          <button onClick={onClose} data-testid="route-replay-close" className="text-slate-400 hover:text-ps-deep p-1"><X size={16} weight="bold" /></button>
        </div>
        <div className="relative" style={{ height: 500 }}>
          {loading && <div className="absolute inset-0 grid place-items-center text-slate-500 text-xs z-[1000] pointer-events-none">Loading…</div>}
          {err && <div className="absolute top-2 left-2 right-2 z-[1000] bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded">{err}</div>}
          <MapContainer center={center} zoom={9} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true} attributionControl={true}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OSM' />
            {path.length > 1 && <Polyline positions={path} pathOptions={{ color: "#0ea5e9", weight: 4, opacity: 0.85 }} />}
            {snaps.map((s, i) => (i === 0 || i === snaps.length - 1) && (
              <CircleMarker key={s.id} center={[s.lat, s.lng]} radius={i === 0 ? 8 : 10}
                pathOptions={{ color: i === 0 ? "#10b981" : "#ef4444", fillColor: i === 0 ? "#10b981" : "#ef4444", fillOpacity: 0.9 }}>
                <Popup>{i === 0 ? "Start" : "End"} · {s.ts.slice(0, 19).replace("T", " ")}<br />{s.address || "—"}</Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 text-xs text-slate-600 flex items-center gap-4 font-mono-data tabular flex-wrap">
          <span data-testid="route-replay-snaps"><b>{snaps.length}</b> snapshots</span>
          <span data-testid="route-replay-distance"><b>{distKm.toFixed(1)}</b> km driven</span>
          {snaps[0] && <span>start {snaps[0].ts.slice(0, 16).replace("T", " ")}</span>}
          {snaps[snaps.length - 1] && <span>end {snaps[snaps.length - 1].ts.slice(0, 16).replace("T", " ")}</span>}
          <span className="ml-auto text-slate-400">Max 30-day range · capped at 5,000 points</span>
        </div>
      </div>
    </div>
  );
}
